import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, ChevronRight, Download, Lightbulb, Loader2, MonitorPlay, Play, RefreshCw,
} from 'lucide-react'
import { api, ApiError, fileToken } from '../api'
import type {
  BrandFilmAspect, BrandFilmConcept, BrandFilmConfig, BrandFilmDetail as Detail,
  BrandFilmJob, BrandFilmServiceJob, BrandFilmStatus, Role,
} from '../types'
import { atLeast } from '../types'
import { go } from '../router'
import {
  Badge, Button, Card, CardTitle, EmptyState, ErrorText, Field, Input, Note,
  SectionLabel, Skeleton, Textarea, toneFor,
} from '../components/ui'
import { Select } from '../components/select'
import { ago, cn } from '../lib/utils'

// Brand films are code-rendered motion-graphics product films, made by the
// separate brandfilm service. ReelWorks proxies it: this page orders films,
// lists this workspace's jobs and shows each one's log, QC and output.

const LIVE = new Set<BrandFilmStatus>(['queued', 'running'])
const HAS_OUTPUT = new Set<BrandFilmStatus>(['done', 'qc_failed'])
const ASPECTS: { value: BrandFilmAspect; label: string }[] = [
  { value: '16:9', label: '16:9 — landscape' },
  { value: '9:16', label: '9:16 — vertical' },
  { value: '1:1', label: '1:1 — square' },
]
const STATUS_LABEL: Record<BrandFilmStatus, string> = {
  queued: 'Queued', running: 'Running', done: 'Done',
  qc_failed: 'QC failed', invalid: 'Invalid spec', failed: 'Failed',
}

const base = (ws: string) => `/api/workspaces/${ws}/brandfilms`

function FilmStatus({ status }: { status: BrandFilmStatus }) {
  return (
    <Badge tone={toneFor(status)} dot={LIVE.has(status)} className="normal-case">
      {STATUS_LABEL[status] ?? status}
    </Badge>
  )
}

function useBrandFilmConfig(ws: string) {
  return useQuery({
    queryKey: ['brandfilms-config', ws],
    queryFn: () => api.get<BrandFilmConfig>(`${base(ws)}/config`),
    // Styles and kits change when someone edits the service's files, not
    // during a session.
    staleTime: 5 * 60_000,
  })
}

export function BrandFilms({ ws, role }: { ws: string; role?: Role }) {
  const config = useBrandFilmConfig(ws)
  const jobs = useQuery({
    queryKey: ['brandfilms', ws],
    queryFn: () => api.get<BrandFilmJob[]>(`${base(ws)}/jobs`),
    // Poll while anything is rendering; the list request refreshes those rows
    // from the service.
    refetchInterval: q =>
      (q.state.data as BrandFilmJob[] | undefined)?.some(j => LIVE.has(j.status)) ? 4000 : false,
  })
  const canMake = atLeast(role, 'member')
  const rows = jobs.data ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl">Brand films</h1>
        <p className="mt-1 text-sm text-ink-faint">
          Motion-graphics product films, written, rendered and QC'd from your product's real screens.
        </p>
      </div>

      {config.isLoading && <Skeleton className="h-64 w-full" />}
      <ErrorText error={config.error} />

      {config.data && !config.data.enabled && (
        <EmptyState
          icon={MonitorPlay}
          title="Brand films are not enabled"
          body={config.data.message ?? 'The brand film service is not configured on this installation.'}
        />
      )}

      {config.data?.enabled && canMake && <CreatePanel ws={ws} config={config.data} />}

      {(config.data?.enabled || rows.length > 0) && (
        <section className="space-y-2">
          <SectionLabel>Films</SectionLabel>
          <ErrorText error={jobs.error} />
          {jobs.isLoading && [0, 1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
          {!jobs.isLoading && rows.length === 0 && (
            <Card className="py-8 text-center text-sm text-ink-faint">
              No films yet.{canMake ? ' Describe one above, or ask for concepts to start from.' : ''}
            </Card>
          )}
          {rows.map(j => (
            <button
              key={j.id}
              onClick={() => go({ name: 'brand-film', id: j.id })}
              className="panel group flex w-full items-center gap-4 p-4 text-left transition-colors hover:border-line-strong hover:bg-surface-2/40"
            >
              <FilmStatus status={j.status} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{j.title || 'Untitled film'}</div>
                <div className="mt-0.5 truncate text-xs text-ink-faint">
                  {j.kind === 'render' ? 'Re-render' : describeRequest(j.request)}
                  {' · '}Created {ago(j.created_at)}{j.creator_name ? ` by ${j.creator_name}` : ''}
                </div>
              </div>
              <ChevronRight className="size-4 shrink-0 text-ink-faint transition-transform group-hover:translate-x-0.5" />
            </button>
          ))}
        </section>
      )}
    </div>
  )
}

function describeRequest(req: Record<string, unknown>) {
  const parts = [req.style, req.aspect, req.seconds ? `${req.seconds}s` : ''].filter(Boolean)
  return parts.length ? parts.join(' · ') : 'Film'
}

// --- create -------------------------------------------------------------------

function CreatePanel({ ws, config }: { ws: string; config: BrandFilmConfig }) {
  const qc = useQueryClient()
  const [kit, setKit] = useState(config.kits[0]?.id ?? '')
  const [style, setStyle] = useState(config.styles[0]?.id ?? '')
  const [aspect, setAspect] = useState<BrandFilmAspect>(config.styles[0]?.aspects?.[0] ?? '16:9')
  const [seconds, setSeconds] = useState('')
  const [title, setTitle] = useState('')
  const [brief, setBrief] = useState('')
  const [focus, setFocus] = useState('')
  const [picked, setPicked] = useState<number | null>(null)

  const current = config.styles.find(s => s.id === style)
  const range = current?.lengthSeconds

  // A style that does not offer the chosen aspect would be rendered in its own
  // default instead, which is a surprise best avoided at the form.
  function chooseStyle(id: string) {
    setStyle(id)
    const next = config.styles.find(s => s.id === id)
    if (next?.aspects?.length && !next.aspects.includes(aspect)) setAspect(next.aspects[0])
  }

  const concepts = useMutation({
    mutationFn: () => api.post<BrandFilmConcept[]>(`${base(ws)}/concepts`, { kit, n: 6, focus: focus.trim() }),
    onSuccess: () => setPicked(null),
  })

  const create = useMutation({
    mutationFn: () => api.post<{ job: BrandFilmJob }>(`${base(ws)}/films`, {
      title: title.trim(),
      kit, style, aspect,
      brief: brief.trim(),
      seconds: seconds ? Number(seconds) : undefined,
    }),
    onSuccess: res => {
      qc.invalidateQueries({ queryKey: ['brandfilms', ws] })
      go({ name: 'brand-film', id: res.job.id })
    },
  })

  function applyConcept(c: BrandFilmConcept, i: number) {
    setPicked(i)
    setTitle(c.title)
    if (config.styles.some(s => s.id === c.style)) setStyle(c.style)
    if (ASPECTS.some(a => a.value === c.aspect)) setAspect(c.aspect)
    setSeconds(c.seconds ? String(c.seconds) : '')
    setBrief(c.brief)
  }

  const secondsValue = seconds ? Number(seconds) : null
  const secondsError = secondsValue != null && (!(secondsValue > 0) || secondsValue > 180)
    ? 'Between 1 and 180 seconds' : null
  const ready = !!kit && !!style && brief.trim().length > 0 && !secondsError

  return (
    <Card className="space-y-5">
      <CardTitle>New film</CardTitle>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Product kit" help="The product whose screens and facts the film is built from.">
          <Select
            ariaLabel="Product kit"
            value={kit}
            onValueChange={setKit}
            placeholder="No kits on the service"
            items={config.kits.map(k => ({ value: k.id, label: k.name || k.id }))}
          />
        </Field>
        <Field label="Style" help={current?.about}>
          <Select
            ariaLabel="Style"
            value={style}
            onValueChange={chooseStyle}
            placeholder="No styles on the service"
            items={config.styles.map(s => ({ value: s.id, label: s.label || s.id, hint: s.about }))}
          />
        </Field>
        <Field label="Aspect">
          <Select
            ariaLabel="Aspect"
            value={aspect}
            onValueChange={v => setAspect(v as BrandFilmAspect)}
            items={ASPECTS.map(a => ({
              ...a,
              hint: current?.aspects && !current.aspects.includes(a.value)
                ? `${current.label} is not designed for ${a.value}` : undefined,
            }))}
          />
        </Field>
        <Field
          label="Length (seconds)"
          error={secondsError}
          help={range ? `${current?.label} runs ${range[0]}–${range[1]}s. Leave blank to let the writer decide.` : 'Leave blank to let the writer decide.'}
        >
          <Input
            type="number" min={1} max={180} inputMode="numeric"
            value={seconds} onChange={e => setSeconds(e.target.value)}
            placeholder={range ? `${range[0]}–${range[1]}` : 'Auto'}
          />
        </Field>
      </div>

      <Field label="Title" help="Your label for the list. Optional.">
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Scheduler launch cut" />
      </Field>

      <Field label="Brief" help="What the film is about and who it is for, in two or three sentences.">
        <Textarea
          rows={4} value={brief} onChange={e => setBrief(e.target.value)}
          placeholder="A 20-second vertical cut showing how the calendar view saves a social team an afternoon a week…"
        />
      </Field>

      <div className="space-y-3 rounded-[--radius-control] border border-line bg-surface-2/40 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Field label="Need a starting point?" className="flex-1" help="Optionally steer the ideas, e.g. “launch of the AI caption writer”.">
            <Input value={focus} onChange={e => setFocus(e.target.value)} placeholder="Focus (optional)" />
          </Field>
          <Button
            variant="secondary" className="sm:mb-[1.35rem]"
            onClick={() => concepts.mutate()} disabled={!kit || concepts.isPending}
          >
            {concepts.isPending ? <Loader2 className="animate-spin" /> : <Lightbulb />}
            {concepts.isPending ? 'Thinking…' : 'Suggest concepts'}
          </Button>
        </div>
        {concepts.isPending && (
          <p className="text-xs text-ink-faint">Writing concepts from the kit. This can take up to a minute.</p>
        )}
        <ErrorText error={concepts.error} />
        {concepts.data && concepts.data.length > 0 && (
          <div className="grid gap-3 md:grid-cols-2">
            {concepts.data.map((c, i) => (
              <button
                key={`${c.title}-${i}`}
                onClick={() => applyConcept(c, i)}
                className={cn(
                  'panel space-y-2 p-4 text-left transition-colors hover:border-line-strong hover:bg-surface-2/40',
                  picked === i && 'border-brand/60 shadow-[inset_2px_0_0_var(--color-brand)]',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium">{c.title}</span>
                  <span className="flex shrink-0 gap-1">
                    <Badge>{config.styles.find(s => s.id === c.style)?.label ?? c.style}</Badge>
                    <Badge>{c.aspect}</Badge>
                  </span>
                </div>
                {c.hook && <p className="text-sm text-ink">“{c.hook}”</p>}
                {c.angle && <p className="text-xs text-ink-faint">{c.angle}</p>}
                {c.outline && c.outline.length > 0 && (
                  <ol className="list-decimal space-y-0.5 pl-4 text-xs text-ink-dim">
                    {c.outline.map((line, n) => <li key={n}>{line}</li>)}
                  </ol>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <ErrorText error={create.error} />
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => create.mutate()} disabled={!ready || create.isPending}>
          {create.isPending ? <Loader2 className="animate-spin" /> : <Play />}
          {create.isPending ? 'Starting…' : 'Make film'}
        </Button>
      </div>
    </Card>
  )
}

// --- detail -------------------------------------------------------------------

export function BrandFilmDetail({ ws, id, role }: { ws: string; id: string; role?: Role }) {
  const detail = useQuery({
    queryKey: ['brandfilm', ws, id],
    queryFn: () => api.get<Detail>(`${base(ws)}/jobs/${id}`),
    refetchInterval: q => LIVE.has((q.state.data as Detail | undefined)?.job.status as BrandFilmStatus) ? 2000 : false,
  })

  if (detail.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }
  const data = detail.data
  if (!data) return <ErrorText error={detail.error} />

  const { job, service } = data
  const status = (service?.status ?? job.status) as BrandFilmStatus
  const live = LIVE.has(status)
  const aspect = (job.request.aspect as string | undefined)
    ?? ((job.request.spec as Record<string, unknown> | undefined)?.aspect as string | undefined)

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" className="-ml-3" onClick={() => go({ name: 'brand-films' })}>
        <ArrowLeft /> Brand films
      </Button>

      <div className="min-w-0">
        <h1 className="truncate text-2xl" title={job.title}>{job.title || 'Untitled film'}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-faint">
          <FilmStatus status={status} />
          <span>·</span>
          <span>{job.kind === 'render' ? 'Re-render of an edited spec' : describeRequest(job.request)}</span>
          <span>·</span>
          <span>Created {ago(job.created_at)}{job.creator_name ? ` by ${job.creator_name}` : ''}</span>
        </div>
      </div>

      {data.service_error && <Note tone="warn">{data.service_error}</Note>}
      {service?.error && <ErrorText error={service.error} />}

      {HAS_OUTPUT.has(status) && <Player ws={ws} id={id} aspect={aspect} />}

      {service && <QCSummary service={service} />}

      <section className="space-y-2">
        <SectionLabel>Log</SectionLabel>
        <LogTail lines={service?.log ?? []} live={live} />
      </section>

      {!live && (
        <SpecEditor ws={ws} job={job} service={service} canRender={atLeast(role, 'member')} />
      )}
    </div>
  )
}

function Player({ ws, id, aspect }: { ws: string; id: string; aspect?: string }) {
  // A <video> src cannot send a header, so the film is authorised by the
  // short-lived media token in the query, and streams rather than buffering
  // the whole file as a blob first.
  const [url, setUrl] = useState('')
  useEffect(() => {
    let dead = false
    fileToken()
      .then(t => { if (!dead) setUrl(`${base(ws)}/jobs/${id}/film.mp4?t=${encodeURIComponent(t)}`) })
      .catch(() => {})
    return () => { dead = true }
  }, [ws, id])

  const vertical = aspect === '9:16'
  const width = vertical ? 'max-w-[300px]' : aspect === '1:1' ? 'max-w-[420px]' : 'max-w-2xl'
  return (
    <section className="space-y-3">
      {url ? (
        <video
          src={url} controls playsInline preload="metadata"
          className={cn('w-full rounded-[--radius-card] border border-line bg-black shadow-[0_16px_48px_oklch(0_0_0/0.45)]', width)}
        />
      ) : (
        <Skeleton className={cn('w-full', width, vertical ? 'aspect-[9/16]' : aspect === '1:1' ? 'aspect-square' : 'aspect-video')} />
      )}
      {url && (
        <Button asChild variant="secondary" size="sm">
          <a href={url} download={`brand-film-${id}.mp4`}><Download /> Download</a>
        </Button>
      )}
    </section>
  )
}

function fmt(n: number | null | undefined, digits = 1) {
  return typeof n === 'number' && Number.isFinite(n) ? n.toFixed(digits) : '—'
}

function QCSummary({ service }: { service: BrandFilmServiceJob }) {
  const qc = service.qc
  const rounds = service.rounds ?? []
  const problems = service.status === 'invalid' ? service.errors ?? [] : service.qc_errors ?? []
  if (!qc && rounds.length === 0 && problems.length === 0) return null

  return (
    <section className="space-y-3">
      <SectionLabel>Quality checks</SectionLabel>
      {qc && (
        <Card className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={qc.pass ? 'good' : 'bad'}>{qc.pass ? 'QC passed' : 'QC failed'}</Badge>
            <span className="text-xs text-ink-faint">
              {qc.errors} error{qc.errors === 1 ? '' : 's'} · {qc.warnings} warning{qc.warnings === 1 ? '' : 's'}
            </span>
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <Stat label="Loudness" value={`${fmt(qc.lufs)} LUFS`} hint="target −14" />
            <Stat label="True peak" value={`${fmt(qc.true_peak)} dBTP`} hint="max −1.0" />
            <Stat label="Duration" value={`${fmt(qc.duration)}s`} />
            <Stat label="Review score" value={fmt(qc.review_score, 0)} />
          </dl>
        </Card>
      )}

      {problems.length > 0 && (
        <Card className="space-y-1.5">
          <div className="text-[13px] font-medium text-ink-dim">
            {service.status === 'invalid' ? 'Spec errors' : 'QC errors'}
          </div>
          <ul className="space-y-1 text-xs text-bad">
            {problems.map((p, i) => <li key={i} className="break-words">{p}</li>)}
          </ul>
        </Card>
      )}

      {rounds.length > 0 && (
        <Card className="space-y-2">
          <div className="text-[13px] font-medium text-ink-dim">Review rounds</div>
          <div className="space-y-1">
            {rounds.map(r => (
              <div key={r.round} className="flex flex-wrap items-baseline gap-x-3 text-sm">
                <span className="w-16 shrink-0 text-ink-faint">Round {r.round + 1}</span>
                <span className="tabular-nums">score {r.score ?? '—'}</span>
                <span className="text-xs text-ink-faint">
                  {r.audit_issues} layout issue{r.audit_issues === 1 ? '' : 's'} · {r.notes?.length ?? 0} note{r.notes?.length === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </section>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <dt className="text-xs text-ink-faint">{label}</dt>
      <dd className="tabular-nums text-ink">{value}</dd>
      {hint && <dd className="text-[11px] text-ink-faint">{hint}</dd>}
    </div>
  )
}

function LogTail({ lines, live }: { lines: string[]; live: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  // Follow the tail while the film renders; leave the scroll alone afterwards.
  useEffect(() => {
    if (live && ref.current) ref.current.scrollTop = ref.current.scrollHeight
  }, [lines.length, live])

  if (lines.length === 0) {
    return (
      <Card className="py-8 text-center text-sm text-ink-faint">
        {live ? 'Waiting for the service to start this film…' : 'No log.'}
      </Card>
    )
  }
  return (
    <div ref={ref} className="panel max-h-80 overflow-y-auto p-3 font-mono text-[12px] leading-relaxed">
      {lines.map((line, i) => (
        <div key={i} className="whitespace-pre-wrap break-words py-0.5 text-ink-dim">{line}</div>
      ))}
      {live && <Loader2 className="mt-1 size-3.5 animate-spin text-brand" />}
    </div>
  )
}

function SpecEditor({ ws, job, service, canRender }: {
  ws: string
  job: BrandFilmJob
  service: BrandFilmServiceJob | null
  canRender: boolean
}) {
  const qc = useQueryClient()
  // The rendered spec when there is one; for a render that never produced
  // one (an invalid spec), the spec it was asked to render.
  const requested = (job.request.spec ?? service?.request?.spec) as unknown
  const spec = useQuery({
    queryKey: ['brandfilm-spec', ws, job.id],
    queryFn: async () => {
      try {
        return await api.get<unknown>(`${base(ws)}/jobs/${job.id}/spec.json`)
      } catch (e) {
        if (e instanceof ApiError && e.status === 404 && requested) return requested
        throw e
      }
    },
    retry: false,
  })
  const loaded = useMemo(() => (spec.data ? JSON.stringify(spec.data, null, 2) : ''), [spec.data])
  const [text, setText] = useState('')
  useEffect(() => { setText(loaded) }, [loaded])

  let parseError = ''
  let parsed: unknown = null
  if (text.trim()) {
    try {
      parsed = JSON.parse(text)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) parseError = 'The spec must be a JSON object.'
    } catch (e) {
      parseError = e instanceof Error ? e.message : 'Invalid JSON'
    }
  }

  const render = useMutation({
    mutationFn: () => api.post<{ job: BrandFilmJob }>(`${base(ws)}/renders`, {
      title: `${job.title || 'Film'} (edited)`,
      spec: parsed,
    }),
    onSuccess: res => {
      qc.invalidateQueries({ queryKey: ['brandfilms', ws] })
      go({ name: 'brand-film', id: res.job.id })
    },
  })

  if (spec.isLoading) return <Skeleton className="h-64 w-full" />
  if (spec.error && !requested) {
    return (
      <section className="space-y-2">
        <SectionLabel>Spec</SectionLabel>
        <Card className="py-6 text-center text-sm text-ink-faint">
          {spec.error instanceof ApiError && spec.error.status === 404
            ? 'This job produced no spec to edit.'
            : (spec.error as Error).message}
        </Card>
      </section>
    )
  }

  const dirty = text !== loaded
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <SectionLabel>Spec</SectionLabel>
        {dirty && (
          <Button variant="ghost" size="sm" onClick={() => setText(loaded)}>Revert</Button>
        )}
      </div>
      <Textarea
        value={text}
        onChange={e => setText(e.target.value)}
        readOnly={!canRender}
        rows={20}
        spellCheck={false}
        aria-label="Film spec JSON"
        className="font-mono text-[12px] leading-relaxed"
      />
      {parseError && <p className="text-xs text-bad">{parseError}</p>}
      <ErrorText error={render.error} />
      {canRender && (
        <div className="flex items-center justify-end gap-3">
          <span className="text-xs text-ink-faint">
            Renders the spec as written: no rewrite, then the same QC.
          </span>
          <Button
            variant="primary"
            onClick={() => render.mutate()}
            disabled={!parsed || !!parseError || render.isPending}
          >
            {render.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            {render.isPending ? 'Starting…' : 'Re-render'}
          </Button>
        </div>
      )}
    </section>
  )
}
